<script context="module"></script>

<script>import { onMount } from "svelte";
import { getBlockComponentOptions } from "../../functions/get-block-component-options";
import { getProcessedBlock } from "../../functions/get-processed-block";
import "../dynamic-div.svelte";
import { bindAnimations } from "./animator";
import { getComponent, getRepeatItemData, provideBuilderBlock, provideBuilderContext, provideLinkComponent, provideRegisteredComponents, } from "./block.helpers";
import BlockStyles from "./components/block-styles.svelte";
import BlockWrapper from "./components/block-wrapper.svelte";
import ComponentRef from "./components/component-ref/component-ref.svelte";
import RepeatedBlock from "./components/repeated-block.svelte";
export let block;
export let context;
export let registeredComponents;
export let linkComponent;
$: blockComponent = () => {
    return getComponent({
        block: block,
        context: $context,
        registeredComponents: registeredComponents,
    });
};
$: repeatItem = () => {
    return getRepeatItemData({
        block: block,
        context: $context,
    });
};
$: processedBlock = () => {
    return block.repeat?.collection
        ? block
        : getProcessedBlock({
            block: block,
            localState: $context.localState,
            rootState: $context.rootState,
            rootSetState: $context.rootSetState,
            context: $context.context,
            shouldEvaluateBindings: true,
        });
};
$: Tag = () => {
    const shouldUseLink = block.tagName === "a" ||
        processedBlock().properties?.href ||
        processedBlock().href;
    if (shouldUseLink) {
        return linkComponent || "a";
    }
    return block.tagName || "div";
};
$: canShowBlock = () => {
    if (block.repeat?.collection) {
        if (repeatItem?.()?.length)
            return true;
        return false;
    }
    const shouldHide = "hide" in processedBlock() ? processedBlock().hide : false;
    const shouldShow = "show" in processedBlock() ? processedBlock().show : true;
    return shouldShow && !shouldHide;
};
$: childrenWithoutParentComponent = () => {
    /**
     * When there is no `componentRef`, there might still be children that need to be rendered. In this case,
     * we render them outside of `componentRef`.
     * NOTE: We make sure not to render this if `repeatItemData` is non-null, because that means we are rendering an array of
     * blocks, and the children will be repeated within those blocks.
     */
    const shouldRenderChildrenOutsideRef = !blockComponent?.()?.component && !repeatItem();
    return shouldRenderChildrenOutsideRef
        ? processedBlock().children ?? []
        : [];
};
$: componentRefProps = () => {
    return {
        blockChildren: processedBlock().children ?? [],
        componentRef: blockComponent?.()?.component,
        componentOptions: {
            ...getBlockComponentOptions(processedBlock()),
            ...provideBuilderBlock(blockComponent(), processedBlock()),
            ...provideBuilderContext(blockComponent(), context),
            ...provideLinkComponent(blockComponent(), linkComponent),
            ...provideRegisteredComponents(blockComponent(), registeredComponents),
        },
        context: context,
        linkComponent: linkComponent,
        registeredComponents: registeredComponents,
        builderBlock: processedBlock(),
        includeBlockProps: blockComponent?.()?.noWrap === true,
        isInteractive: !blockComponent?.()?.isRSC,
    };
};
onMount(() => {
    const blockId = processedBlock().id;
    const animations = processedBlock().animations;
    if (animations && blockId) {
        bindAnimations(animations.map((animation) => ({
            ...animation,
            elementId: blockId,
        })));
    }
});
</script>

{#if canShowBlock()}
  <BlockStyles {block} context={$context} />

  {#if !blockComponent?.()?.noWrap}
    {#if !repeatItem()}
      <BlockWrapper Wrapper={Tag()} block={processedBlock()} {context}
        ><ComponentRef
          componentRef={componentRefProps().componentRef}
          componentOptions={componentRefProps().componentOptions}
          blockChildren={componentRefProps().blockChildren}
          context={componentRefProps().context}
          registeredComponents={componentRefProps().registeredComponents}
          linkComponent={componentRefProps().linkComponent}
          builderBlock={componentRefProps().builderBlock}
          includeBlockProps={componentRefProps().includeBlockProps}
          isInteractive={componentRefProps().isInteractive}
        />
        {#each childrenWithoutParentComponent() as child (child.id)}
          <svelte:self
            block={child}
            {registeredComponents}
            {linkComponent}
            {context}
          />
        {/each}
      </BlockWrapper>
    {:else}
      {#each repeatItem() as data, index (index)}
        <RepeatedBlock
          repeatContext={data.context}
          block={data.block}
          {registeredComponents}
          {linkComponent}
        />
      {/each}
    {/if}
  {:else}
    <ComponentRef
      componentRef={componentRefProps().componentRef}
      componentOptions={componentRefProps().componentOptions}
      blockChildren={componentRefProps().blockChildren}
      context={componentRefProps().context}
      registeredComponents={componentRefProps().registeredComponents}
      linkComponent={componentRefProps().linkComponent}
      builderBlock={componentRefProps().builderBlock}
      includeBlockProps={componentRefProps().includeBlockProps}
      isInteractive={componentRefProps().isInteractive}
    />
  {/if}
{/if}